/**
 * Render a timestamp as a human-friendly relative-time string.
 *
 * Round-5 M5: shared with ScanTypeCard + SecurityCheckCard so the same
 * "5 min ago" / "Just now" / "2 days ago" formatting renders across the
 * security UI. Pre-extraction, ScanTypeCard had this and SecurityCheckCard
 * fell through to `toLocaleString()` which produces ISO-like strings —
 * inconsistent on the same screen for the round-4 H1 lastPatchAttemptAt
 * field.
 *
 * Round-5 L17: `Math.max(0, diffMs)` clamps server-clock-skew-ahead-of-client
 * cases (would otherwise render `-5 min ago`).
 */
export interface RelativeTimeOptions {
  /**
   * `'second'` surfaces "40s ago" for values that change while you watch (live telemetry); the
   * default `'minute'` rounds anything under a minute to "Just now".
   *
   * This option exists so there is ONE relative-time vocabulary. There used to be two
   * near-duplicate formatters that disagreed on sub-minute wording, on the hour abbreviation
   * ("2 hours ago" vs "2 h ago") and on the >7-day fallback — and both rendered on the same
   * dashboard, so the roster said "1 min ago" while the map card said "40s ago" about the same
   * device.
   */
  precision?: 'minute' | 'second'
  /**
   * Explicit clock. A ticking component derives every age on screen from ONE `now`, so rows can
   * never disagree with each other by a render.
   */
  now?: number
}

export function formatRelativeTime(
  timestamp: string | Date | null | undefined,
  options: RelativeTimeOptions = {},
): string {
  if (!timestamp) return 'Never'

  const { precision = 'minute', now = Date.now() } = options
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  const diffMs = Math.max(0, now - date.getTime())

  if (Number.isNaN(diffMs)) return 'Never'

  const diffSecs = Math.round(diffMs / 1000)
  const diffMins = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (precision === 'second' && diffSecs < 5) return 'Just now'
  if (precision === 'second' && diffSecs < 60) return `${diffSecs}s ago`
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} min ago`
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`

  return date.toLocaleDateString()
}
